import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { gbp } from "../lib/format";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";
import { TemplateLinesEditor, computeTemplatePhase } from "./TemplateLines";

/* LT-21b — read-only, priced preview of a job template (New Tender → "View").
 * Same grid as the editor / estimate, every £ at today's library rates. */
export function TemplatePreviewModal({ templateId, open, onClose }) {
  const navigate = useNavigate();
  const { labelFor } = useJobTypeLibrary();
  const [tpl, setTpl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !templateId) return;
    setTpl(null); setError(null);
    api.get(`/templates/${templateId}`).then((r) => setTpl(r.data)).catch(() => setError("Couldn't load the template."));
  }, [open, templateId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const phases = tpl?.phases || [];
  const totals = phases.map(computeTemplatePhase);
  const grand = totals.reduce((s, t) => s + t.phase_total, 0);
  const lineCount = totals.reduce((s, t) => s + t.line_count, 0);

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-start justify-center pt-10 px-4" onClick={onClose} data-testid="template-preview-backdrop">
      <div className="w-full max-w-[1500px] max-h-[calc(100vh-80px)] flex flex-col bg-surface border border-line rounded-lg shadow-card overflow-hidden"
        onClick={(e) => e.stopPropagation()} data-testid="template-preview-modal">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-line">
          <div className="min-w-0">
            <div className="col-head text-ink-3" style={{ fontSize: 9.5 }}>Job template preview</div>
            <h2 className="font-display text-ink truncate" style={{ fontSize: 18, fontWeight: 600 }} data-testid="template-preview-name">
              {tpl ? tpl.name : "Loading…"}
            </h2>
            {tpl && (
              <p className="text-ink-3 mt-0.5" style={{ fontSize: 12 }} data-testid="template-preview-summary">
                {tpl.job_type ? <>Default for <strong>{labelFor(tpl.job_type)}</strong> · </> : null}
                {phases.length} phase{phases.length === 1 ? "" : "s"} · {lineCount} default line{lineCount === 1 ? "" : "s"}
                {" · "}<span className="font-mono tnum text-ink" style={{ fontWeight: 600 }}>{gbp(grand)}</span> at today's library rates
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {tpl && (
              <button type="button" onClick={() => navigate(`/templates/${tpl.id}`)} data-testid="template-preview-open-editor"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line text-ink-2 hover:text-ink hover:bg-surface-2" style={{ fontSize: 12 }}>
                <ExternalLink size={12} /> Open in editor
              </button>
            )}
            <button type="button" onClick={onClose} data-testid="template-preview-close"
              className="p-1.5 rounded-md text-ink-3 hover:text-ink hover:bg-surface-2" aria-label="Close preview">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto scrollbar-thin flex-1 bg-bg">
          {error && <p className="px-5 py-8 text-note-red text-center" style={{ fontSize: 13 }}>{error}</p>}
          {!tpl && !error && (
            <div className="flex items-center justify-center gap-2 py-16 text-ink-3" style={{ fontSize: 13 }}>
              <Loader2 size={14} className="animate-spin" /> Loading template…
            </div>
          )}
          {tpl && phases.length === 0 && (
            <p className="px-5 py-10 text-ink-3 text-center" style={{ fontSize: 13 }}>This template has no phases yet — a tender started from it gets only the system phases.</p>
          )}
          {tpl && phases.map((p, i) => (
            <div key={i} className="mx-4 my-4 bg-surface border border-line rounded-lg overflow-hidden" data-testid={`template-preview-phase-${i}`}>
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-line bg-surface-2/50">
                <div className="flex items-baseline gap-3 min-w-0">
                  <span className="font-mono tnum text-ink-3 flex-shrink-0" style={{ fontSize: 11.5 }}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-ink truncate" style={{ fontSize: 15, fontWeight: 600 }}>{p.phase_name}</span>
                </div>
                {totals[i].line_count > 0 && (
                  <span className="font-mono tnum text-ink-3 flex-shrink-0" style={{ fontSize: 11.5 }}>
                    {totals[i].line_count} item{totals[i].line_count === 1 ? "" : "s"}
                    <span className="text-ink ml-2" style={{ fontWeight: 600 }}>{gbp(totals[i].phase_total)}</span>
                  </span>
                )}
              </div>
              <TemplateLinesEditor phase={p} phaseIndex={i} onChange={() => {}} readOnly />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-4 px-5 py-3 border-t border-line bg-surface">
          <span className="text-ink-3" style={{ fontSize: 11.5 }}>Read-only. A new tender snapshots these rates the moment it is created.</span>
          <button type="button" onClick={onClose} data-testid="template-preview-done"
            className="h-8 px-3.5 rounded-md bg-ink text-white hover:bg-ink-2" style={{ fontSize: 12.5, fontWeight: 500 }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
